import { useTranslation } from 'react-i18next'
import type { WorkingHours } from './useGymBranding'
import { DAYS_OF_WEEK, getTodayKey, getTimezoneCity } from './gymHours'

interface Props {
  workingHours: WorkingHours
  timezone: string
  isOpen: boolean
  onClose: () => void
}

export function WorkingHoursModal({ workingHours, timezone, isOpen, onClose }: Props) {
  const { t } = useTranslation()

  if (!isOpen) return null

  const todayKey = getTodayKey(timezone)
  const city = getTimezoneCity(timezone)

  return (
    <div
      className="fixed inset-0 z-[var(--z-overlay)] flex items-center justify-center bg-black/50 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-background p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">{t('gymHours.modalTitle')}</h2>
          <button
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-full text-foreground/50 hover:bg-content2 hover:text-foreground"
            aria-label={t('common.close')}
          >
            ×
          </button>
        </div>

        <ul className="space-y-1">
          {DAYS_OF_WEEK.map((day) => {
            const schedule = workingHours[day]
            const isToday = day === todayKey
            return (
              <li
                key={day}
                className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${
                  isToday ? 'bg-primary/10 font-medium' : ''
                }`}
              >
                <span className={isToday ? 'text-primary' : 'text-foreground/70'}>
                  {t(`gymHours.${day}`)}
                </span>
                {schedule.closed ? (
                  <span className="text-foreground/40">{t('gymHours.closed')}</span>
                ) : (
                  <span>
                    {schedule.open} – {schedule.close}
                  </span>
                )}
              </li>
            )
          })}
        </ul>

        <p className="mt-4 text-center text-xs text-foreground/40">
          {t('gymHours.timesShownIn', { city })}
        </p>
      </div>
    </div>
  )
}
